import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Shield, Database, Cookie, UserCheck, Mail, Clock, Lock } from 'lucide-react';

const Datenschutz = () => {
  const { language } = useLanguage();

  const content = {
    sv: {
      title: "Integritetspolicy",
      subtitle: "Hur vi hanterar dina personuppgifter enligt GDPR",
      back: "Tillbaka till startsidan",
      lastUpdated: "Senast uppdaterad: Januari 2026",
      sections: [
        {
          icon: Shield,
          title: "1. Personuppgiftsansvarig",
          content: "Skogskalkyl är personuppgiftsansvarig för behandlingen av dina personuppgifter. Vi är engagerade i att skydda din integritet och behandla dina uppgifter i enlighet med EU:s allmänna dataskyddsförordning (GDPR) och tillämplig svensk lag."
        },
        {
          icon: Database,
          title: "2. Vilka uppgifter samlar vi in?",
          content: "Vi samlar in följande kategorier av personuppgifter:\n\n• Kontouppgifter: Namn, e-postadress\n• Användningsdata: Sparade scenarier, beräkningshistorik\n• Tekniska uppgifter: IP-adress (anonymiserad), webbläsartyp, språkinställningar\n\nVi samlar INTE in:\n• Betalningsuppgifter\n• Platsdata\n• Personnummer eller känsliga uppgifter"
        },
        {
          icon: UserCheck,
          title: "3. Rättslig grund för behandling",
          content: "Vi behandlar dina personuppgifter baserat på:\n\n• Avtal: För att tillhandahålla våra tjänster till dig\n• Berättigat intresse: För att förbättra och säkra våra tjänster\n• Samtycke: För marknadsföring (om du har samtyckt)"
        },
        {
          icon: Lock,
          title: "4. Datasäkerhet",
          content: "Vi vidtar lämpliga tekniska och organisatoriska åtgärder för att skydda dina personuppgifter:\n\n• Kryptering av data vid överföring (TLS/SSL)\n• Hashning av lösenord (bcrypt)\n• Begränsad åtkomst till personuppgifter\n• Regelbunden säkerhetsgranskning"
        },
        {
          icon: Clock,
          title: "5. Lagringstid",
          content: "Vi lagrar dina personuppgifter så länge som krävs för att tillhandahålla våra tjänster:\n\n• Kontoinformation: Så länge ditt konto är aktivt\n• Sparade scenarier: Tills du raderar dem eller ditt konto\n• Loggfiler: Högst 12 månader"
        },
        {
          icon: UserCheck,
          title: "6. Dina rättigheter",
          content: "Enligt GDPR har du följande rättigheter:\n\n• Rätt till tillgång: Begära information om dina uppgifter\n• Rätt till rättelse: Korrigera felaktiga uppgifter\n• Rätt till radering: \"Rätten att bli glömd\"\n• Rätt till dataportabilitet: Få ut dina uppgifter\n• Rätt att invända: Mot viss behandling\n• Rätt att återkalla samtycke\n\nKontakta oss på dataskydd@skogskalkyl.se för att utöva dina rättigheter."
        },
        {
          icon: Cookie,
          title: "7. Cookies",
          content: "Vi använder endast nödvändiga cookies för att:\n\n• Hålla dig inloggad (sessionscookie)\n• Spara dina språkinställningar\n\nVi använder INGA tredjepartscookies eller spårningscookies."
        },
        {
          icon: Mail,
          title: "8. Kontakt & Klagomål",
          content: "Vid frågor om dataskydd:\n\nE-post: dataskydd@skogskalkyl.se\n\nDu har rätt att lämna klagomål till tillsynsmyndigheten:\nIntegritetsskyddsmyndigheten (IMY)\nBox 8114, 104 20 Stockholm\nwww.imy.se"
        }
      ]
    },
    de: {
      title: "Datenschutzerklärung",
      subtitle: "Wie wir Ihre personenbezogenen Daten gemäß DSGVO verarbeiten",
      back: "Zurück zur Startseite",
      lastUpdated: "Letzte Aktualisierung: Januar 2026",
      sections: [
        {
          icon: Shield,
          title: "1. Verantwortlicher",
          content: "Skogskalkyl ist für die Verarbeitung Ihrer personenbezogenen Daten verantwortlich. Wir sind bestrebt, Ihre Privatsphäre zu schützen und Ihre Daten in Übereinstimmung mit der EU-Datenschutz-Grundverordnung (DSGVO) zu verarbeiten."
        },
        {
          icon: Database,
          title: "2. Welche Daten erheben wir?",
          content: "Wir erheben folgende Kategorien personenbezogener Daten:\n\n• Kontodaten: Name, E-Mail-Adresse\n• Nutzungsdaten: Gespeicherte Szenarien, Berechnungshistorie\n• Technische Daten: IP-Adresse (anonymisiert), Browsertyp, Spracheinstellungen\n\nWir erheben KEINE:\n• Zahlungsdaten\n• Standortdaten\n• Sensible personenbezogene Daten"
        },
        {
          icon: UserCheck,
          title: "3. Rechtsgrundlage",
          content: "Wir verarbeiten Ihre Daten auf Basis von:\n\n• Vertrag: Um Ihnen unsere Dienste bereitzustellen\n• Berechtigtes Interesse: Zur Verbesserung und Sicherung unserer Dienste\n• Einwilligung: Für Marketing (falls Sie eingewilligt haben)"
        },
        {
          icon: Lock,
          title: "4. Datensicherheit",
          content: "Wir treffen angemessene technische und organisatorische Maßnahmen:\n\n• Verschlüsselung bei der Übertragung (TLS/SSL)\n• Passwort-Hashing (bcrypt)\n• Zugriffsbeschränkungen\n• Regelmäßige Sicherheitsüberprüfungen"
        },
        {
          icon: Clock,
          title: "5. Speicherdauer",
          content: "Wir speichern Ihre Daten so lange wie für die Diensterbringung erforderlich:\n\n• Kontoinformationen: Solange Ihr Konto aktiv ist\n• Gespeicherte Szenarien: Bis zur Löschung durch Sie\n• Protokolldateien: Maximal 12 Monate"
        },
        {
          icon: UserCheck,
          title: "6. Ihre Rechte",
          content: "Gemäß DSGVO haben Sie folgende Rechte:\n\n• Auskunftsrecht (Art. 15 DSGVO)\n• Recht auf Berichtigung (Art. 16 DSGVO)\n• Recht auf Löschung (Art. 17 DSGVO)\n• Recht auf Datenübertragbarkeit (Art. 20 DSGVO)\n• Widerspruchsrecht (Art. 21 DSGVO)\n• Recht auf Widerruf der Einwilligung\n\nKontaktieren Sie uns unter datenschutz@skogskalkyl.se"
        },
        {
          icon: Cookie,
          title: "7. Cookies",
          content: "Wir verwenden nur notwendige Cookies für:\n\n• Anmeldesitzungen\n• Spracheinstellungen\n\nWir verwenden KEINE Tracking- oder Drittanbieter-Cookies."
        },
        {
          icon: Mail,
          title: "8. Kontakt & Beschwerden",
          content: "Bei Datenschutzfragen:\n\nE-Mail: datenschutz@skogskalkyl.se\n\nBeschwerderecht bei der Aufsichtsbehörde:\nIntegritetsskyddsmyndigheten (IMY)\nwww.imy.se"
        }
      ]
    },
    en: {
      title: "Privacy Policy",
      subtitle: "How we handle your personal data according to GDPR",
      back: "Back to homepage",
      lastUpdated: "Last updated: January 2026",
      sections: [
        {
          icon: Shield,
          title: "1. Data Controller",
          content: "Skogskalkyl is the data controller for the processing of your personal data. We are committed to protecting your privacy and processing your data in accordance with the EU General Data Protection Regulation (GDPR)."
        },
        {
          icon: Database,
          title: "2. What data do we collect?",
          content: "We collect the following categories of personal data:\n\n• Account data: Name, email address\n• Usage data: Saved scenarios, calculation history\n• Technical data: IP address (anonymized), browser type, language settings\n\nWe do NOT collect:\n• Payment information\n• Location data\n• Sensitive personal data"
        },
        {
          icon: UserCheck,
          title: "3. Legal basis for processing",
          content: "We process your personal data based on:\n\n• Contract: To provide our services to you\n• Legitimate interest: To improve and secure our services\n• Consent: For marketing (if you have consented)"
        },
        {
          icon: Lock,
          title: "4. Data security",
          content: "We implement appropriate technical and organizational measures:\n\n• Encryption during transmission (TLS/SSL)\n• Password hashing (bcrypt)\n• Access restrictions\n• Regular security audits"
        },
        {
          icon: Clock,
          title: "5. Retention period",
          content: "We store your data for as long as necessary to provide our services:\n\n• Account information: As long as your account is active\n• Saved scenarios: Until deleted by you\n• Log files: Maximum 12 months"
        },
        {
          icon: UserCheck,
          title: "6. Your rights",
          content: "Under GDPR you have the following rights:\n\n• Right of access\n• Right to rectification\n• Right to erasure (\"right to be forgotten\")\n• Right to data portability\n• Right to object\n• Right to withdraw consent\n\nContact us at privacy@skogskalkyl.se to exercise your rights."
        },
        {
          icon: Cookie,
          title: "7. Cookies",
          content: "We only use necessary cookies for:\n\n• Login sessions\n• Language preferences\n\nWe do NOT use tracking or third-party cookies."
        },
        {
          icon: Mail,
          title: "8. Contact & Complaints",
          content: "For data protection inquiries:\n\nEmail: privacy@skogskalkyl.se\n\nRight to lodge a complaint with supervisory authority:\nSwedish Authority for Privacy Protection (IMY)\nwww.imy.se"
        }
      ]
    },
    se: {
      title: "Priváhtavuođapolitihkka",
      subtitle: "Mo mii gieđahallat du persovdnadieđuid GDPR mielde",
      back: "Ruoktot álgosiidui",
      lastUpdated: "Maŋimustá ođasmahtton: Ođđajagimánnu 2026",
      sections: [
        {
          icon: Shield,
          title: "1. Dieđuid hálddašeaddji",
          content: "Skogskalkyl lea dieđuid hálddašeaddji du persovdnadieđuid gieđahallamii."
        },
        {
          icon: Database,
          title: "2. Makkár dieđuid mii čohkket?",
          content: "Mii čohkket čuovvovaš persovdnadieđuid:\n\n• Kontodieđut: Namma, e-poasta\n• Geavahandieđut: Vurkon skenárijot"
        },
        {
          icon: Lock,
          title: "3. Dieđusihkarvuohta",
          content: "Mii váldit heivvolaš teknihkalaš ja organisatoralaš doaibmabijuid."
        },
        {
          icon: UserCheck,
          title: "4. Du vuoigatvuođat",
          content: "GDPR mielde dus leat vuoigatvuođat:\n\n• Vuoigatvuohta oažžut dieđuid\n• Vuoigatvuohta divvut\n• Vuoigatvuohta sihkkut"
        }
      ]
    }
  };

  const t = content[language] || content.sv;

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="datenschutz-page">
      <Link 
        to="/" 
        className="inline-flex items-center text-sm text-forest-600 hover:text-forest-700"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        {t.back}
      </Link>

      <div>
        <h1 className="text-3xl font-heading font-bold text-forest-800">{t.title}</h1>
        <p className="text-muted-foreground mt-2">{t.subtitle}</p>
        <p className="text-sm text-muted-foreground mt-1">{t.lastUpdated}</p>
      </div>

      <div className="space-y-6">
        {t.sections.map((section, index) => (
          <Card key={index}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <section.icon className="h-5 w-5 text-forest-600" />
                {section.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                {section.content}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Datenschutz;
